import { useQueries, useQuery } from '@tanstack/react-query';
import {
  type BankMovementResponse,
  type EmployeeListItem,
  formatMoney,
  fromCents,
  SALARY_MONTHS,
  SALARY_PAYMENT_KIND_LABELS,
  SALARY_PAYMENT_KINDS,
  salaryPeriodLabel,
  type SalaryPaymentKind,
  type SalaryProposals,
  type SalaryYearResponse,
  toCents,
} from '@k-management/shared';
import { Fragment, useEffect } from 'react';
import { Link } from 'react-router';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Icon } from '../../components/Icon.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { moneyOf, peopleKeys } from '../people/people-ui.js';

export interface SalaryLine {
  employeeId: string;
  year: number;
  month: number;
  kind: SalaryPaymentKind;
  /** As typed. */
  amount: string;
  /** The month or the employee changed: the amount follows what is left (1.67). */
  refill?: boolean;
}

const SOURCES: Record<NonNullable<SalaryProposals['source']>, string> = {
  iban: 'riconosciuto dall’IBAN del dipendente',
  nome: 'riconosciuto dal nome',
  mese: 'bonifico cumulativo: i dipendenti con lo stipendio del mese da pagare',
};

/** The cents of the lines, null when an amount cannot be read. */
export function salaryLinesCents(lines: readonly SalaryLine[]): number | null {
  let total = 0;
  for (const line of lines) {
    const amount = moneyOf(line.amount);
    if (amount === null) return null;
    total += toCents(amount);
  }
  return total;
}

/**
 * The salaries a transfer pays (08/10/2026, 1.35): one line per employee with
 * the month, advance or balance, and the amount; first filled by the
 * proposals (IBAN or name in the description, or every employee with the
 * month to pay), then free to change. For who has the module Persone.
 */
export function SalaryLines({
  code,
  movement,
  lines,
  onChange,
}: {
  code: string;
  movement: BankMovementResponse;
  lines: SalaryLine[];
  onChange: (lines: SalaryLine[]) => void;
}) {
  const proposals = useQuery({
    queryKey: ['treasury', 'banks', code, 'salary-proposals', movement.id],
    queryFn: () =>
      apiRequest<SalaryProposals>(
        `/treasury/banks/${code}/movements/${movement.id}/salary-proposals`,
      ),
  });
  const employees = useQuery({
    queryKey: ['people', 'employees', 'dipendenti'],
    queryFn: () =>
      apiRequest<EmployeeListItem[]>('/people/employees').then((items) =>
        // Collaborators are paid on their invoices (2.38).
        items.filter((item) => item.relationship === 'dipendente'),
      ),
  });
  // The proposals arrive: they are the first lines. Not those kept from an
  // earlier opening, made on nets changed since (1.69): the ones read again now.
  useEffect(() => {
    if (!proposals.data || proposals.isFetching || lines.length) return;
    onChange(
      proposals.data.lines.map((line) => ({
        employeeId: line.employeeId,
        year: line.year,
        month: line.month,
        kind: line.kind,
        amount: line.amount,
      })),
    );
    // Only when the proposals arrive.
  }, [proposals.data, proposals.isFetching]);

  // The salaries of the years the lines touch, for what each month still owes.
  const years = [...new Set(lines.map((line) => `${line.employeeId}|${line.year}`))].map((key) => {
    const [employeeId, year] = key.split('|');
    return { employeeId: employeeId!, year: Number(year) };
  });
  const salaries = useQueries({
    queries: years.map((item) => ({
      queryKey: peopleKeys.salaries(item.employeeId, item.year),
      queryFn: () =>
        apiRequest<SalaryYearResponse>(
          `/people/employees/${item.employeeId}/salaries?year=${item.year}`,
        ),
    })),
  });
  const monthOf = (line: SalaryLine) => {
    const at = years.findIndex(
      (item) => item.employeeId === line.employeeId && item.year === line.year,
    );
    return salaries[at]?.data?.months.find((item) => item.month === line.month) ?? null;
  };

  const bookingYear = Number(movement.bookingDate.slice(0, 4));
  const bookingMonth = Number(movement.bookingDate.slice(5, 7));
  const periods: { year: number; month: number }[] = [];
  for (const year of [bookingYear - 1, bookingYear])
    for (const month of SALARY_MONTHS) periods.push({ year, month });
  periods.push({ year: bookingYear + 1, month: 1 });
  const movementCents = Math.abs(toCents(movement.amount));
  const total = salaryLinesCents(lines);
  const set = (index: number, change: Partial<SalaryLine>) =>
    onChange(lines.map((line, at) => (at === index ? { ...line, ...change } : line)));
  const people = (employees.data ?? []).filter(
    (item) => item.leftOn === null || item.leftOn >= `${bookingYear - 1}-01-01`,
  );

  // A line with a new month or employee takes what that month still owes, up
  // to the part of the transfer not given to the other lines (1.67).
  const loaded = lines.map((line) => (line.refill ? monthOf(line) : null));
  useEffect(() => {
    if (!lines.some((line, index) => line.refill && loaded[index])) return;
    onChange(
      lines.map((line, index) => {
        const month = loaded[index];
        if (!line.refill || !month) return line;
        if (month.net === null) return { ...line, refill: false };
        const others = salaryLinesCents(lines.filter((_line, at) => at !== index)) ?? 0;
        const free = Math.max(0, movementCents - others);
        const residual = Math.max(0, toCents(month.residual ?? '0'));
        return { ...line, amount: fromCents(Math.min(residual, free)), refill: false };
      }),
    );
  }, [lines, loaded.map((month) => (month ? `${month.net}|${month.residual}` : '')).join(';')]);

  return (
    <div className="stack salary-lines">
      {proposals.isFetching && lines.length === 0 && (
        <p className="muted small">Ricerca dei dipendenti…</p>
      )}
      {proposals.data?.source && <p className="muted small">{SOURCES[proposals.data.source]}</p>}
      {proposals.data && !proposals.data.source && lines.length === 0 && (
        <p className="muted small">
          Nessun dipendente riconosciuto: aggiungilo qui sotto, o conferma solo il motivo.
        </p>
      )}
      {lines.map((line, index) => (
        <Fragment key={index}>
          <div className="salary-line">
            <select
              aria-label="Dipendente"
              value={line.employeeId}
              onChange={(event) => set(index, { employeeId: event.target.value, refill: true })}
            >
              {people.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.lastName} {item.firstName}
                </option>
              ))}
            </select>
            <select
              aria-label="Mensilità"
              value={`${line.year}-${line.month}`}
              onChange={(event) => {
                const [year, month] = event.target.value.split('-').map(Number);
                set(index, { year: year!, month: month!, refill: true });
              }}
            >
              {periods.map((period) => (
                <option
                  key={`${period.year}-${period.month}`}
                  value={`${period.year}-${period.month}`}
                >
                  {salaryPeriodLabel(period.year, period.month)}
                </option>
              ))}
            </select>
            <select
              aria-label="Acconto o saldo"
              value={line.kind}
              onChange={(event) => set(index, { kind: event.target.value as SalaryPaymentKind })}
            >
              {SALARY_PAYMENT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {SALARY_PAYMENT_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
            <DecimalInput
              aria-label="Importo"
              value={line.amount}
              onChange={(value) => set(index, { amount: value })}
              decimals={[2, 2]}
            />
            <button
              type="button"
              className="icon-button"
              title="Togli la riga"
              aria-label="Togli la riga"
              onClick={() => onChange(lines.filter((_line, at) => at !== index))}
            >
              <Icon name="close" />
            </button>
          </div>
          <SalaryDue line={line} month={monthOf(line)} />
        </Fragment>
      ))}
      {people.length > 0 && (
        <button
          type="button"
          className="link"
          onClick={() =>
            onChange([
              ...lines,
              {
                employeeId:
                  people.find((item) => !lines.some((line) => line.employeeId === item.id))?.id ??
                  people[0]!.id,
                year: bookingYear,
                month: bookingMonth,
                kind: 'saldo',
                amount: total === null ? '' : fromCents(Math.max(0, movementCents - total)),
              },
            ])
          }
        >
          + aggiungi un dipendente
        </button>
      )}
      {employees.data && people.length === 0 && (
        <p className="muted small">Nessun dipendente in Organizzazione › Persone.</p>
      )}
      {lines.length > 0 && (
        <div className="reconcile-sum">
          <table>
            <tbody>
              <tr>
                <td>Assegnato</td>
                <td>{total === null ? '—' : formatMoney(fromCents(total))}</td>
              </tr>
              <tr>
                <td>Movimento</td>
                <td>{formatMoney(fromCents(movementCents))}</td>
              </tr>
              <tr className="total">
                <td>
                  Differenza
                  {total !== null &&
                    total !== movementCents &&
                    (total < movementCents ? ': resta da assegnare' : ': supera il movimento')}
                </td>
                <td className={total === movementCents ? 'ok' : 'warn'}>
                  {total === null ? '—' : formatMoney(fromCents(movementCents - total))}
                  {total === movementCents && ' ✓'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * What the month of a line still owes (08/10/2026, 1.36): the net of the
 * payslip, the advances and the balance already paid, what is left, and what
 * will be left after this transfer.
 */
function SalaryDue({
  line,
  month,
}: {
  line: SalaryLine;
  month: SalaryYearResponse['months'][number] | null;
}) {
  if (!month) return <p className="muted small salary-due">Lettura della mensilità…</p>;
  const period = salaryPeriodLabel(line.year, line.month);
  if (month.net === null) {
    return (
      <p className="notice small salary-due">
        Netto di {period} non ancora caricato: un acconto si conferma lo stesso e verrà scalato dal
        netto quando lo caricherai (
        <Link to={`/organizzazione/persone/${line.employeeId}`}>dalla busta paga</Link>).
        {toCents(month.advances) + toCents(month.paid) > 0 &&
          ` Già pagati ${formatMoney(fromCents(toCents(month.advances) + toCents(month.paid)))}.`}
      </p>
    );
  }
  const amount = moneyOf(line.amount);
  const residual = toCents(month.residual ?? '0');
  const after = amount === null ? null : residual - toCents(amount);
  const advances = month.payments.filter((payment) => payment.kind === 'acconto');
  return (
    <div className="reconcile-sum salary-due">
      <table>
        <tbody>
          <tr>
            <td>Netto in busta di {period}</td>
            <td>{formatMoney(month.net)}</td>
          </tr>
          <tr>
            <td>
              Acconti già dati
              {advances.length > 0 &&
                ` (${advances.map((payment) => formatDate(payment.date).slice(0, 5)).join(', ')})`}
            </td>
            <td>− {formatMoney(month.advances)}</td>
          </tr>
          <tr>
            <td>Saldo già pagato</td>
            <td>− {formatMoney(month.paid)}</td>
          </tr>
          <tr className="total">
            <td>Resta da pagare</td>
            <td>{formatMoney(fromCents(residual))}</td>
          </tr>
          <tr>
            <td>Con questo bonifico</td>
            <td>− {amount === null ? '—' : formatMoney(amount)}</td>
          </tr>
          <tr className="total">
            <td>
              Resterà
              {after !== null && after < 0 && ': si paga più del dovuto'}
            </td>
            <td className={after === 0 ? 'ok' : 'warn'}>
              {after === null ? '—' : formatMoney(fromCents(after))}
              {after === 0 && ' ✓'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
